/**
 * A handshake celebrates once for this player, whoever offered and whoever
 * accepted. A photo uses the same update, so a handshake already seen — at the
 * accept, or because it was confirmed when this screen opened — does not
 * celebrate again.
 */
const seen = new Set<string>();

export interface HandshakeCelebrationRow {
  id: string;
  isConfirmed: boolean;
  initiatorId: string | null;
  responderId: string | null;
}

export function rememberHandshake(id: string | null | undefined): void {
  if (id) seen.add(id);
}

/** True when this is the first time this handshake is celebrated. */
export function celebrateHandshake(id: string | null | undefined, fire: () => void): boolean {
  if (!id || seen.has(id)) return false;

  seen.add(id);
  fire();
  return true;
}

/** Confirmed already, with both people on it. A passerby still waiting is not one. */
function isMeeting(handshake: HandshakeCelebrationRow): boolean {
  return handshake.isConfirmed && !!handshake.initiatorId && !!handshake.responderId;
}

function playerIsIn(handshake: HandshakeCelebrationRow, userId: string): boolean {
  return handshake.initiatorId === userId || handshake.responderId === userId;
}

/** Handshakes that are already a meeting when a screen first loads. */
export function rememberConfirmedHandshakes(handshakes: HandshakeCelebrationRow[] | null | undefined): void {
  for (const handshake of handshakes ?? []) {
    if (isMeeting(handshake)) rememberHandshake(handshake.id);
  }
}

/**
 * Either player, and only when this list is the first to show the meeting.
 * The first list, with no previous one, is remembered and not celebrated.
 * Returns whether confetti was thrown.
 */
export function celebrateNewlyConfirmedHandshakes(
  previous: HandshakeCelebrationRow[] | null | undefined,
  next: HandshakeCelebrationRow[] | null | undefined,
  userId: string | null | undefined,
  fire: () => void,
): boolean {
  if (!next) return false;
  if (!previous) {
    rememberConfirmedHandshakes(next);
    return false;
  }
  if (!userId) return false;

  let fired = false;
  for (const handshake of next) {
    if (!isMeeting(handshake) || !playerIsIn(handshake, userId)) continue;
    const wasMeeting = previous.some((older) => older.id === handshake.id && isMeeting(older));
    if (wasMeeting) continue;
    if (celebrateHandshake(handshake.id, fire)) fired = true;
  }

  return fired;
}
